import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Container } from "../components/Container";
import { useI18n } from "../i18n/LanguageProvider";
import { Reveal } from "../components/Reveal";
import { Breadcrumbs } from "../components/Breadcrumbs";

const slug = (i) => `s-${i + 1}`;

/** Premium shared layout for /privacy, /terms and /cookies. */
export default function LegalPage({ kind }) {
  const { t, locale } = useI18n();
  const lx = t("legalx") || {};
  const data = kind === "cookies" ? t("legalx.cookies") : (lx[kind] || t(`legal.${kind}`));
  const L = (p) => `/${locale}${p}`;

  const metaTitleKey = kind === "cookies" ? "legalx.cookiesMetaTitle" : `meta.${kind}.title`;
  const metaDescKey = kind === "cookies" ? "legalx.cookiesMetaDesc" : `meta.${kind}.description`;

  const related = [
    { key: "privacy", to: L("/privacy"), label: lx.navPrivacy },
    { key: "terms", to: L("/terms"), label: lx.navTerms },
    { key: "cookies", to: L("/cookies"), label: lx.navCookies },
    { key: "support", to: L("/support"), label: lx.navSupport },
  ].filter((r) => r.key !== kind);

  return (
    <div className="bg-white" data-testid={`legal-page-${kind}`}>
      <Seo titleKey={metaTitleKey} descKey={metaDescKey} path={`/${kind}`} pageType={kind} breadcrumbs={[{ name: t("footer.links.home"), path: "" }, { name: data.title, path: `/${kind}` }]} />
      <Header />
      <Breadcrumbs items={[{ name: t("footer.links.home"), to: `/${locale}` }, { name: data.title }]} />
      <main className="pt-6 md:pt-8">
        {/* Header band */}
        <div className="relative overflow-hidden bg-[#EAF7F0]">
          <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-green/20 blur-3xl" />
          <Container className="relative py-14 md:py-20">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-1.5 w-1.5 rounded-full brand-gradient" />
              <span className="text-xs font-medium uppercase tracking-[0.2em] text-black/50">{lx.eyebrow}</span>
            </div>
            <Reveal>
              <h1 className="max-w-3xl text-4xl font-semibold leading-[1.05] tracking-tighter text-black sm:text-5xl lg:text-6xl">
                {data.title}
              </h1>
            </Reveal>
            {data.intro && (
              <Reveal delay={0.06}>
                <p className="mt-6 max-w-2xl text-lg leading-relaxed text-black/55">{data.intro}</p>
              </Reveal>
            )}
            <Reveal delay={0.1}>
              <p className="mt-6 text-sm text-black/40">{data.updated}</p>
            </Reveal>
          </Container>
        </div>

        {/* Body with sticky TOC */}
        <Container className="grid grid-cols-1 gap-x-16 gap-y-12 py-16 md:py-24 lg:grid-cols-[240px_1fr]">
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-black/40">{lx.contents}</p>
            <nav className="flex flex-col border-l border-black/10">
              {data.body.map((s, i) => (
                <a
                  key={i}
                  href={`#${slug(i)}`}
                  className="-ml-px border-l-2 border-transparent py-1.5 pl-4 text-sm text-black/55 transition-colors hover:border-green hover:text-black"
                >
                  {s.h}
                </a>
              ))}
            </nav>
          </aside>

          <div className="max-w-2xl">
            {data.body.map((s, i) => (
              <Reveal key={i} delay={0.02 * i}>
                <section id={slug(i)} className="scroll-mt-28 border-b border-black/[0.06] py-7 first:pt-0 last:border-0">
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-xs text-green">{String(i + 1).padStart(2, "0")}</span>
                    <h2 className="text-xl font-semibold tracking-tight text-black sm:text-2xl">{s.h}</h2>
                  </div>
                  <p className="mt-3 text-base leading-[1.8] text-black/60">{s.p}</p>
                </section>
              </Reveal>
            ))}

            <div className="mt-10 rounded-3xl border border-black/[0.08] bg-[#F6F8F7] p-8">
              <p className="text-sm text-black/55">{lx.questions}</p>
              <a
                href="mailto:support@centauros.app"
                className="mt-2 inline-block text-lg font-medium text-black underline decoration-green decoration-2 underline-offset-4 transition-colors hover:text-green"
              >
                support@centauros.app
              </a>
              <div className="mt-6 flex flex-wrap gap-2">
                {related.map((r) => (
                  <Link
                    key={r.key}
                    to={r.to}
                    className="rounded-full border border-black/10 px-4 py-2 text-sm font-medium text-black/70 transition-colors hover:border-black/30 hover:text-black"
                  >
                    {r.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
}
